/**
 * Today's Coming up, on the profile: how many days ahead it looks at what
 * the forecast holds for the plants, 0 up to the server's most
 * (limits.COMING_UP_MIN_DAYS to COMING_UP_MAX_DAYS: the Grow setting today.HEADS_UP_DAYS's range, 0 to 7 as set today), the Grow settings' number
 * (today.HEADS_UP_DAYS) marked as the usual one; choosing it follows the
 * setting. Kept the moment it changes, with "Saved" beside it.
 *
 * It was a part of Weather warnings ("On Today: Coming up"), where nobody
 * looking for it would think to look; it is a row of its own now, under
 * This app.
 *
 * Used by: UserProfile (?s=comingup). Saves to the account's preferences
 * (headsUpDays) through the user-config store.
 *
 * A save that fails on our side (a 5xx or no answer) says the standard
 * sentence, reported to Sentry, with the support address as a link (D31);
 * any other failure keeps profile.preferences.toast.saveFailed.
 *
 * @module ComingUpScreen
 */

import React from 'react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useUserConfig, type Preferences } from '@/shared/context/userConfigStore';
import { ProfileScreen } from './ProfileScreen';
import { SettingList, SettingRow } from './profileKit';
import { useSavedTicks } from './useSavedTicks';
import { useComingUpDays } from './useComingUpDays';
import { useGrowLimits } from '@/shared/lib/growSettings';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';

/** The Coming up screen: how many days ahead it looks. */
export const ComingUpScreen: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { reactTranslate: t } = useTranslation();
  const { userConfig, savePreferences } = useUserConfig();
  const { shown, usual, words } = useComingUpDays();
  /** The days ahead a gardener can pick for Coming up: the server's range (limits.COMING_UP_MIN_DAYS to COMING_UP_MAX_DAYS), 0 being today only. */
  const limits = useGrowLimits();
  const first = limits.COMING_UP_MIN_DAYS ?? 0;
  // Always includes the days now shown, so a saved choice is selected whatever the range.
  const last = Math.max(limits.COMING_UP_MAX_DAYS ?? 0, shown ?? 0, first);
  const headsUpChoices = Array.from({ length: Math.max(0, last - first) + 1 }, (_, step) => first + step);
  const { saved, flash } = useSavedTicks();
  const [error, setError] = React.useState<string | null>(null);
  const id = React.useId();

  /**
   * Keeps the days ahead, a null for the usual, then says so beside it.
   *
   * @param days - 0 to 7, or null.
   */
  const keep = async (days: number | null): Promise<void> => {
    setError(null);
    try {
      await savePreferences({ ...userConfig.preferences, headsUpDays: days } as Preferences);
      flash('headsUp');
    } catch (err) {
      setError(errorMessage(err, t('profile.preferences.toast.saveFailed')));
    }
  };

  return (
    <ProfileScreen open={open} title={t('profile.index.comingUp')} onClose={onClose}>
      <SettingList>
        {/* Eight choices, too many for a row of buttons on a phone: a list. The
            usual one is marked, and choosing it follows the Grow settings. */}
        <SettingRow title={t('profile.preferences.headsUp.title')} hint={t('profile.preferences.headsUp.hint')} saved={saved('headsUp')} htmlFor={id} stacked>
          <select
            id={id}
            value={String(shown ?? '')}
            onChange={(event) => {
              const days = Number(event.target.value);
              void keep(days === usual ? null : days);
            }}
            className="h-11 w-full max-w-xs rounded-md border bg-background px-3 text-sm"
          >
            {headsUpChoices.map((days) => (
              <option key={days} value={String(days)}>
                {days === usual ? t('profile.preferences.headsUp.usual', { label: words(days) }) : words(days)}
              </option>
            ))}
          </select>
        </SettingRow>
      </SettingList>
      {error && <p className="text-sm text-destructive"><FailureText text={error} /></p>}
    </ProfileScreen>
  );
};
